import React from 'react';
import { CellSelection, TableMap, findTable, findCellPos } from "prosemirror-tables";
import type { Editor } from "@tiptap/react";

export const TABLE_PICKER_MAX_ROWS = 10;
export const TABLE_PICKER_MAX_COLS = 10;
export const TABLE_PICKER_CELL = 18;
export const TABLE_PICKER_GAP = 4;

export function useTableEditor(
    editor: Editor | null | undefined,
    name: string,
    attrs?: Record<string, any>
) {
    const [tbMenuOpen, setTbMenuOpen] = React.useState(false);
    const [hover, setHover] = React.useState<{ rows: number; cols: number }>({ rows: 0, cols: 0 });

    function getTableCtx(editor: Editor, tablePos: number) {
        const { state } = editor;
        const posInside = Math.min(tablePos + 1, state.doc.content.size);
        const $inside = state.doc.resolve(posInside);

        const table = findTable($inside) || findTable(state.selection.$from);
        if (!table) return null;

        const map = TableMap.get(table.node);
        const start = table.pos + 1;
        return { table, map, start };
    }

    function selectRow(editor: Editor, tablePos: number, rowIndex: number) {
        const ctx = getTableCtx(editor, tablePos);
        if (!ctx) return false;
        const { state, view } = editor;
        const { table, map, start } = ctx;
        const a = start + map.positionAt(rowIndex, 0, table.node);
        const b = start + map.positionAt(rowIndex, map.width - 1, table.node);
        const sel = CellSelection.rowSelection(state.doc.resolve(a), state.doc.resolve(b));
        view.dispatch(state.tr.setSelection(sel));
        view.focus();
        return true;
    }

    function selectCol(editor: Editor, tablePos: number, colIndex: number) {
        const ctx = getTableCtx(editor, tablePos);
        if (!ctx) return false;
        const { state, view } = editor;
        const { table, map, start } = ctx;
        const a = start + map.positionAt(0, colIndex, table.node);
        const b = start + map.positionAt(map.height - 1, colIndex, table.node);
        const sel = CellSelection.colSelection(state.doc.resolve(a), state.doc.resolve(b));
        view.dispatch(state.tr.setSelection(sel));
        view.focus();
        return true;
    }

    function selectRowAtCursor(editor: Editor) {
        const { state, view } = editor;
        const { $from } = state.selection;
        const tbl = findTable($from);
        if (!tbl) return false;

        const $cell = findCellPos(state.doc, $from.pos);
        if (!$cell) return false;

        const map = TableMap.get(tbl.node);
        const tableStart = (tbl as any).start ?? tbl.pos + 1; // tip: start quando disponível
        const rect = map.findCell($cell.pos - tableStart);

        const anchorPos = tableStart + map.positionAt(rect.top, 0, tbl.node);
        const headPos = tableStart + map.positionAt(rect.top, map.width - 1, tbl.node);

        const sel = CellSelection.rowSelection(
            state.doc.resolve(anchorPos),
            state.doc.resolve(headPos),
        );
        view.dispatch(state.tr.setSelection(sel));
        view.focus();
        return true;
    }

    function selectColAtCursor(editor: Editor) {
        const { state, view } = editor;
        const { $from } = state.selection;
        const tbl = findTable($from);
        if (!tbl) return false;

        const $cell = findCellPos(state.doc, $from.pos);
        if (!$cell) return false;

        const map = TableMap.get(tbl.node);
        const tableStart = (tbl as any).start ?? tbl.pos + 1;
        const rect = map.findCell($cell.pos - tableStart);

        const col = rect.left;
        const anchorPos = tableStart + map.positionAt(0, col, tbl.node);
        const headPos = tableStart + map.positionAt(map.height - 1, col, tbl.node);

        const sel = CellSelection.colSelection(
            state.doc.resolve(anchorPos),
            state.doc.resolve(headPos),
        );
        view.dispatch(state.tr.setSelection(sel));
        view.focus();
        return true;
    }

    function colorCurrentRow(editor: Editor, hex: string | null) {
        if (!editor?.isActive("table")) return;
        selectRowAtCursor(editor);
        editor.chain().focus().setCellAttribute("backgroundColor", hex).run(); // aplica à seleção de células
    }

    function colorCurrentCol(editor: Editor, hex: string | null) {
        if (!editor?.isActive("table")) return;
        selectColAtCursor(editor);
        editor.chain().focus().setCellAttribute("backgroundColor", hex).run();
    }

    return { tbMenuOpen, setTbMenuOpen, hover, setHover, selectRow, selectCol, selectRowAtCursor, selectColAtCursor, colorCurrentRow, colorCurrentCol };
}